import { findAreaForModule } from "./learning-areas.ts"
import { GUIDE_LESSONS } from "./lessons.ts"
import { getGuideModule } from "./modules.ts"
import {
  isGuideLessonAccessible,
  isGuideQuestionAccessible
} from "./guide-permissions.ts"
import { getQuestionCategory } from "./question-categories.ts"
import { GUIDE_QUESTIONS } from "./questions.ts"
import type {
  GuideLearningArea,
  GuideLesson,
  GuideModule,
  GuideQuestion,
  GuideQuestionCategoryDef
} from "../types/guide-content.types.ts"

export interface GuideLessonSearchResult {
  type: "lesson"
  lesson: GuideLesson
  module: GuideModule
  area: GuideLearningArea | null
}

export interface GuideQuestionSearchResult {
  type: "question"
  question: GuideQuestion
  category: GuideQuestionCategoryDef
}

export type GuideSearchResult =
  | GuideLessonSearchResult
  | GuideQuestionSearchResult

/**
 * Client-side, over the same content registries everything else reads —
 * lessons AND Q&A, both permission-filtered before the text match ever
 * runs so an inaccessible feature's name or content can never leak through
 * a search result, matching how the rest of the Guide never shows a title
 * or description a `requiredPage`/module gate would hide elsewhere.
 */
export const searchGuideContent = (
  query: string,
  translate: (key: string) => string,
  pages: readonly string[] | null | undefined,
  codes: unknown
): GuideSearchResult[] => {
  const term = query.trim().toLowerCase()
  if (!term) return []

  const results: GuideSearchResult[] = []

  for (const lesson of GUIDE_LESSONS) {
    const module = getGuideModule(lesson.moduleSlug)
    if (!module) continue
    if (!isGuideLessonAccessible(lesson, pages, codes)) continue

    const haystack = [
      translate(lesson.titleKey),
      translate(lesson.descriptionKey),
      translate(module.titleKey)
    ]
      .join(" ")
      .toLowerCase()
    if (!haystack.includes(term)) continue

    results.push({
      type: "lesson",
      lesson,
      module,
      area: findAreaForModule(module.slug)
    })
  }

  for (const question of GUIDE_QUESTIONS) {
    const category = getQuestionCategory(question.category)
    if (!category) continue
    if (!isGuideQuestionAccessible(question, pages, codes)) continue

    const haystack = [
      translate(question.questionKey),
      translate(question.answerKey),
      ...(question.keywords ?? [])
    ]
      .join(" ")
      .toLowerCase()
    if (!haystack.includes(term)) continue

    results.push({ type: "question", question, category })
  }

  return results
}
